import { Link, Navigate, useLocation } from 'react-router-dom';
import type { RouteObject } from "react-router-dom";
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { markStaffDevice } from '@/lib/traffic';
import NotFound from "../pages/NotFound";
import CustomPageRenderer from "../pages/custom/page";
import Home from "../pages/home/page";
import BrandStory from "../pages/brand-story/page";
import ThreeStepTreatment from "../pages/3-step-treatment/page";
import ProductsPage from "../pages/products/page";
import WishlistPage from "../pages/wishlist/page";
import ProductDetailPage from "../pages/products/components/ProductDetailPage";
import ContentHubPage from "../pages/content/page";
import ArticleDetailPage from "../pages/content/components/ArticleDetailPage";
import WhereToBuyPage from "../pages/where-to-buy/page";
import VerifyPage from "../pages/verify/page";
import AboutPage from "../pages/about/page";
import ContactPage from "../pages/contact/page";
import FaqPage from "../pages/faq/page";
import PrivacyPolicyPage from "../pages/privacy-policy/page";
import DataDeletionPage from "../pages/data-deletion/page";
import TermsOfServicePage from "../pages/terms-of-service/page";
import ShippingReturnsPage from "../pages/shipping-returns/page";
import CheckoutPage from '../pages/checkout/page';
import PaymentResultPage from '../pages/checkout/payment/page';
import OrderConfirmationPage from '../pages/order/page';
import PartnerApplyPage from '../pages/partner-apply/page';
import PartnerApplySuccessPage from '../pages/partner-apply/success/page';
import LoginPage from '../pages/login/page';
import RegisterPage from '../pages/register/page';
import ForgotPasswordPage from '../pages/forgot-password/page';
import MyDashboardPage from '../pages/my/page';
import MyProfilePage from '../pages/my/profile/page';
import MyOrdersPage from '../pages/my/orders/page';
import MyOrderDetailPage from '../pages/my/orders/[id]/page';
import MyQnAPage from '../pages/my/qna/page';
import ResellerDashboardPage from '../pages/reseller/page';
import ResellerOrdersPage from '../pages/reseller/orders/page';
import ResellerNewOrderPage from '../pages/reseller/order/new/page';
import ResellerProfilePage from '../pages/reseller/profile/page';
import { RequirePagePermission } from './guards';
import { ToastProvider } from '../components/ui/Toast';
import AdminDashboard from '../pages/admin/page';
import AdminInventory from '../pages/admin/inventory/page';
import AdminAnalytics from '../pages/admin/analytics/page';
import AdminOrders from '../pages/admin/orders/page';
import AdminDailySummary from '../pages/admin/daily-summary/page';
import AdminOrderDetail from '../pages/admin/orders/detail/page';
import AdminOrderInvoice from '../pages/admin/orders/detail/invoice/page';
import AdminCustomers from '../pages/admin/customers/page';
import AdminFraudCheck from '../pages/admin/fraud-check/page';
import AdminFollowup from '../pages/admin/followup/page';
import AdminCommunications from '../pages/admin/communications/page';
import AdminVerifyCode from '../pages/admin/verify-code/page';
import AdminQna from '../pages/admin/qna/page';
import AdminWebsiteHub from '../pages/admin/website/page';
import AdminWebsiteHomePage from '../pages/admin/website/home-page/page';
import AdminWebsiteProductsPage from '../pages/admin/website/products-page/page';
import AdminWebsiteHeaderFooter from '../pages/admin/website/header-footer/page';
import AdminWebsiteShippingPage from '../pages/admin/website/shipping-page/page';
import AdminWebsiteSettingsPage from '../pages/admin/website/settings-page/page';
import AdminCustomPages from '../pages/admin/pages/page';
import AdminLandingPages from '../pages/admin/landing-pages/page';
import AdminLandingPageDetail from '../pages/admin/landing-pages/detail/page';
import LandingPageRenderer from '../pages/landing/page';
import AdminRetailers from '../pages/admin/retailers/page';
import AdminActivityLog from '../pages/admin/activity-log/page';
import AdminResellers from '../pages/admin/resellers/page';
import AdminResellerStock from '../pages/admin/resellers/stock/page';
import AdminResellerOrdersPrint from '../pages/admin/resellers/orders-print/page';
import AdminResellerNew from '../pages/admin/resellers/new/page';
import AdminResellerProfileView from '../pages/admin/resellers/view/page';
import AdminResellerApplicationDetail from '../pages/admin/resellers/applications/detail/page';
import AdminArticles from '../pages/admin/articles/page';
import AdminArticleDetail from '../pages/admin/articles/detail/page';
import AdminProducts from '../pages/admin/products/page';
import AdminProductDetail from '../pages/admin/products/detail/page';
import AdminProductReviews from '../pages/admin/reviews/page';
import AdminDiscounts from '../pages/admin/discounts/page';
import AdminDiscountDetail from '../pages/admin/discounts/detail/page';
import AdminCouriers from '../pages/admin/couriers/page';
import AdminCustomPageDetail from '../pages/admin/pages/detail/page';
import AdminCustomerDetail from '../pages/admin/customers/detail/page';
import AdminProfile from '../pages/admin/profile/page';
import AdminNotifications from '../pages/admin/notifications/page';
import AdminNotFound from '../pages/admin/AdminNotFound';
import GoLink from '../pages/go/page';

const baseRoutes: RouteObject[] = [
  { path: '/', element: <Home /> },
  { path: '/brand-story', element: <BrandStory /> },
  { path: '/3-step-treatment', element: <ThreeStepTreatment /> },
  { path: '/products', element: <ProductsPage /> },
  { path: '/products/:slug', element: <ProductDetailPage /> },
  { path: '/wishlist', element: <WishlistPage /> },
  { path: '/content', element: <ContentHubPage /> },
  { path: '/content/:slug', element: <ArticleDetailPage /> },
  { path: '/stores', element: <WhereToBuyPage /> },
  { path: '/verify', element: <VerifyPage /> },
  { path: '/verify/:code', element: <VerifyPage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/contact', element: <ContactPage /> },
  { path: '/faq', element: <FaqPage /> },
  { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
  { path: '/data-deletion', element: <DataDeletionPage /> },
  { path: '/terms-of-service', element: <TermsOfServicePage /> },
  { path: '/shipping-returns', element: <ShippingReturnsPage /> },
  { path: '/description', element: <ProductsPage /> },
  { path: '/checkout', element: <CheckoutPage /> },
  { path: '/checkout/payment/:tranId', element: <PaymentResultPage /> },
  { path: '/order/:id', element: <OrderConfirmationPage /> },
  { path: '/partner-apply', element: <PartnerApplyPage /> },
  { path: '/partner-apply/success', element: <PartnerApplySuccessPage /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/my', element: <MyDashboardPage /> },
  { path: '/my/profile', element: <MyProfilePage /> },
  { path: '/my/orders', element: <MyOrdersPage /> },
  { path: '/my/orders/:id', element: <MyOrderDetailPage /> },
  { path: '/reseller', element: <ResellerDashboardPage /> },
  { path: '/reseller/orders', element: <ResellerOrdersPage /> },
  { path: '/reseller/order/new', element: <ResellerNewOrderPage /> },
  { path: '/reseller/profile', element: <ResellerProfilePage /> },
  { path: '/my/qna', element: <MyQnAPage /> },
  // 301 redirects — old paths → new paths
  { path: '/buy', element: <LangNavigate to="/stores" /> },
  // Common guessed URLs → the real route, so typing e.g. /where-to-buy
  // lands on the page instead of a 404. These static paths outrank /:slug,
  // so an admin custom page can't use these slugs.
  { path: '/where-to-buy', element: <LangNavigate to="/stores" /> },
  { path: '/wheretobuy', element: <LangNavigate to="/stores" /> },
  { path: '/store', element: <LangNavigate to="/stores" /> },
  { path: '/store-locator', element: <LangNavigate to="/stores" /> },
  { path: '/stockists', element: <LangNavigate to="/stores" /> },
  { path: '/shop', element: <LangNavigate to="/products" /> },
  { path: '/product', element: <LangNavigate to="/products" /> },
  { path: '/3-step', element: <LangNavigate to="/3-step-treatment" /> },
  { path: '/three-step', element: <LangNavigate to="/3-step-treatment" /> },
  { path: '/treatment', element: <LangNavigate to="/3-step-treatment" /> },
  { path: '/about-us', element: <LangNavigate to="/about" /> },
  { path: '/our-story', element: <LangNavigate to="/brand-story" /> },
  { path: '/story', element: <LangNavigate to="/brand-story" /> },
  { path: '/contact-us', element: <LangNavigate to="/contact" /> },
  { path: '/faqs', element: <LangNavigate to="/faq" /> },
  { path: '/help', element: <LangNavigate to="/faq" /> },
  { path: '/privacy', element: <LangNavigate to="/privacy-policy" /> },
  { path: '/terms', element: <LangNavigate to="/terms-of-service" /> },
  { path: '/tos', element: <LangNavigate to="/terms-of-service" /> },
  { path: '/shipping', element: <LangNavigate to="/shipping-returns" /> },
  { path: '/returns', element: <LangNavigate to="/shipping-returns" /> },
  { path: '/refund-policy', element: <LangNavigate to="/shipping-returns" /> },
  { path: '/blog', element: <LangNavigate to="/content" /> },
  { path: '/articles', element: <LangNavigate to="/content" /> },
  { path: '/signin', element: <LangNavigate to="/login" /> },
  { path: '/sign-in', element: <LangNavigate to="/login" /> },
  { path: '/signup', element: <LangNavigate to="/register" /> },
  { path: '/sign-up', element: <LangNavigate to="/register" /> },
  { path: '/account', element: <LangNavigate to="/my" /> },
  { path: '/my-account', element: <LangNavigate to="/my" /> },
  { path: '/orders', element: <LangNavigate to="/my/orders" /> },
  { path: '/partner', element: <LangNavigate to="/partner-apply" /> },
  { path: '/become-a-partner', element: <LangNavigate to="/partner-apply" /> },
  { path: '/authenticity', element: <LangNavigate to="/verify" /> },
  { path: '/products/hydro-booster', element: <Navigate to="/products/skin-priming-booster-essence" replace /> },
  { path: '/products/intensive-repair-serum', element: <Navigate to="/products/daily-rescue-serum" replace /> },
  { path: '/products/barrier-shield-cream', element: <Navigate to="/products/barrier-seal-cream" replace /> },
  // Ad landing pages (see /admin/landing-pages) — no site nav/footer,
  // never linked from anywhere on the main site, reached only via a direct
  // ad-click URL. Deliberately separate from /:slug's CustomPageRenderer
  // (different chrome, different block set — see src/pages/landing/page.tsx).
  { path: '/lp/:slug', element: <LandingPageRenderer /> },
  // Admin-created custom pages (see /admin/pages) — a static path anywhere
  // above always wins over this dynamic one, so it can never shadow a real
  // route regardless of array order (React Router ranks static segments
  // higher than params).
  { path: '/:slug', element: <CustomPageRenderer /> },
  { path: '/go/:code', element: <GoLink /> },
  { path: '*', element: <NotFound /> },
];

// Redirect that keeps the /ko or /bn prefix it was reached under — the
// ko/bn route copies below reuse baseRoutes' elements, so a plain
// <Navigate to="/stores"> would drop a Korean/Bengali visitor back to English.
function LangNavigate({ to }: { to: string }) {
  const lang = useLocation().pathname.match(/^\/(ko|bn)(\/|$)/)?.[1];
  return <Navigate to={lang ? `/${lang}${to}` : to} replace />;
}

const koRoutes: RouteObject[] = baseRoutes
  .filter((r) => r.path && r.path !== '*')
  .map((r) => {
    const Component = () => r.element;
    return {
      path: `/ko${r.path}`,
      element: <Component />,
    };
  });

// Korean redirects for old product slugs
const koRedirects: RouteObject[] = [
  { path: '/ko/products/hydro-booster', element: <Navigate to="/ko/products/skin-priming-booster-essence" replace /> },
  { path: '/ko/products/intensive-repair-serum', element: <Navigate to="/ko/products/daily-rescue-serum" replace /> },
  { path: '/ko/products/barrier-shield-cream', element: <Navigate to="/ko/products/barrier-seal-cream" replace /> },
];

const bnRoutes: RouteObject[] = baseRoutes
  .filter((r) => r.path && r.path !== '*')
  .map((r) => {
    const Component = () => r.element;
    return {
      path: `/bn${r.path}`,
      element: <Component />,
    };
  });

// Bengali redirects for old product slugs
const bnRedirects: RouteObject[] = [
  { path: '/bn/products/hydro-booster', element: <Navigate to="/bn/products/skin-priming-booster-essence" replace /> },
  { path: '/bn/products/intensive-repair-serum', element: <Navigate to="/bn/products/daily-rescue-serum" replace /> },
  { path: '/bn/products/barrier-shield-cream', element: <Navigate to="/bn/products/barrier-seal-cream" replace /> },
];

// Staff admin console, merged in from the standalone rxk-admin app so the
// storefront and its back office share one deployment (matching the Pick K /
// Medi K pattern). Gated by RequirePagePermission — per-role, per-sidebar-page
// access (see rxk_role_permissions / the Team page) rather than a hardcoded
// role whitelist — and wrapped in admin's own ToastProvider (the storefront
// has a separate toast system — admin pages call the ToastContext copied
// over from rxk-admin, which needs its own provider mounted). Not part of
// the ko/bn i18n duplication above since it's staff-only, not a public page.
function AdminPage({ children }: { children: ReactNode }) {
  return (
    <RequirePagePermission>
      <StaffDeviceMark />
      <ToastProvider>{children}</ToastProvider>
    </RequirePagePermission>
  );
}

// Staff who reach an admin page: this browser's shop visits are not counted.
function StaffDeviceMark() {
  useEffect(() => markStaffDevice(), []);
  return null;
}

const adminRoutes: RouteObject[] = [
  { path: '/admin', element: <AdminPage><AdminDashboard /></AdminPage> },
  { path: '/admin/inventory', element: <AdminPage><AdminInventory /></AdminPage> },
  { path: '/admin/analytics', element: <AdminPage><AdminAnalytics /></AdminPage> },
  { path: '/admin/orders', element: <AdminPage><AdminOrders /></AdminPage> },
  { path: '/admin/daily-summary', element: <AdminPage><AdminDailySummary /></AdminPage> },
  { path: '/admin/orders/:orderId', element: <AdminPage><AdminOrderDetail /></AdminPage> },
  { path: '/admin/orders/:orderId/invoice', element: <AdminPage><AdminOrderInvoice /></AdminPage> },
  { path: '/admin/customers', element: <AdminPage><AdminCustomers /></AdminPage> },
  { path: '/admin/fraud-check', element: <AdminPage><AdminFraudCheck /></AdminPage> },
  { path: '/admin/followup', element: <AdminPage><AdminFollowup /></AdminPage> },
  { path: '/admin/communications', element: <AdminPage><AdminCommunications /></AdminPage> },
  { path: '/admin/verify-code', element: <AdminPage><AdminVerifyCode /></AdminPage> },
  { path: '/admin/customers/:customerId', element: <AdminPage><AdminCustomerDetail /></AdminPage> },
  { path: '/admin/qna', element: <AdminPage><AdminQna /></AdminPage> },
  { path: '/admin/website', element: <AdminPage><AdminWebsiteHub /></AdminPage> },
  { path: '/admin/website/home-page', element: <AdminPage><AdminWebsiteHomePage /></AdminPage> },
  { path: '/admin/website/products-page', element: <AdminPage><AdminWebsiteProductsPage /></AdminPage> },
  { path: '/admin/website/header-footer', element: <AdminPage><AdminWebsiteHeaderFooter /></AdminPage> },
  { path: '/admin/website/shipping-page', element: <AdminPage><AdminWebsiteShippingPage /></AdminPage> },
  { path: '/admin/website/settings-page', element: <AdminPage><AdminWebsiteSettingsPage /></AdminPage> },
  { path: '/admin/pages', element: <AdminPage><AdminCustomPages /></AdminPage> },
  { path: '/admin/landing-pages', element: <AdminPage><AdminLandingPages /></AdminPage> },
  { path: '/admin/landing-pages/:id', element: <AdminPage><AdminLandingPageDetail /></AdminPage> },
  { path: '/admin/retailers', element: <AdminPage><AdminRetailers /></AdminPage> },
  { path: '/admin/activity-log', element: <AdminPage><AdminActivityLog /></AdminPage> },
  { path: '/admin/resellers', element: <AdminPage><AdminResellers /></AdminPage> },
  { path: '/admin/resellers/stock', element: <AdminPage><AdminResellerStock /></AdminPage> },
  { path: '/admin/resellers/orders-print', element: <AdminPage><AdminResellerOrdersPrint /></AdminPage> },
  { path: '/admin/resellers/new', element: <AdminPage><AdminResellerNew /></AdminPage> },
  { path: '/admin/resellers/:partnerId/view', element: <AdminPage><AdminResellerProfileView /></AdminPage> },
  { path: '/admin/resellers/applications/:applicationId', element: <AdminPage><AdminResellerApplicationDetail /></AdminPage> },
  { path: '/admin/articles', element: <AdminPage><AdminArticles /></AdminPage> },
  { path: '/admin/articles/:id', element: <AdminPage><AdminArticleDetail /></AdminPage> },
  { path: '/admin/products', element: <AdminPage><AdminProducts /></AdminPage> },
  { path: '/admin/products/:sku', element: <AdminPage><AdminProductDetail /></AdminPage> },
  { path: '/admin/reviews', element: <AdminPage><AdminProductReviews /></AdminPage> },
  { path: '/admin/discounts', element: <AdminPage><AdminDiscounts /></AdminPage> },
  { path: '/admin/discounts/:id', element: <AdminPage><AdminDiscountDetail /></AdminPage> },
  { path: '/admin/couriers', element: <AdminPage><AdminCouriers /></AdminPage> },
  { path: '/admin/pages/:id', element: <AdminPage><AdminCustomPageDetail /></AdminPage> },
  { path: '/admin/profile', element: <AdminPage><AdminProfile /></AdminPage> },
  { path: '/admin/notifications', element: <AdminPage><AdminNotifications /></AdminPage> },
  { path: '/admin/*', element: <AdminNotFound /> },
];

const routes: RouteObject[] = [...baseRoutes, ...koRoutes, ...koRedirects, ...bnRoutes, ...bnRedirects, ...adminRoutes];

export default routes;